import { createContext, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import InsuranceUnserviceableModal from './components/unserviceable-modal';
import { IUnserviceableInsurance } from './constants';
import _ from 'lodash';
import { stepPayloadHandler } from '../../helpers';
import { CustomersController, CustomersStepTypeEnum } from '@/shared/endpoints/customers';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import { ROUTES } from '@/shared/constants/routes';
import { filterInsurances } from '@/shared/helpers/insurance';

interface InsuranceContextType {
    changeInsuranceConflict: (conflict: IUnserviceableInsurance | null) => void;
}

export const InsuranceContext = createContext<InsuranceContextType>({ changeInsuranceConflict: _.noop });

const InsuranceOutletWithContext = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const sendDataToGTM = useGTMDispatch();
    const user = useAppSelector(selectOnboardingUser);
    const [conflict, setConflict] = useState<IUnserviceableInsurance | null>(null);

    const filteredInsurances = filterInsurances([...(user?.insurances || []), ...(user?.insuranceUploads || [])]);

    const onContinue = async () => {
        if (!user?.userOnboarding?.details) return;

        const payload = _.cloneDeep(user.userOnboarding);
        payload.details = {
            ...payload.details!,
            ...stepPayloadHandler(user?.userOnboarding?.details?.currentStep, CustomersStepTypeEnum.Prescriptions)
        };

        await dispatch(CustomersController.updateMetadata(payload));
        sendDataToGTM({
            event: GtmEvent.INSURANCE_ADDED,
            userId: user?.userProfile?.accountId,
            count: user && filteredInsurances.length
        });
        navigate(`/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MEDBOX_HUE}`);
    };

    return (
        <InsuranceContext.Provider value={{ changeInsuranceConflict: setConflict }}>
            <div>
                <Outlet />

                <InsuranceUnserviceableModal
                    conflict={conflict}
                    hasInsurance={!!filteredInsurances.length}
                    onAdd={() => setConflict(null)}
                    onClose={() => setConflict(null)}
                    onContinue={() => {
                        setConflict(null);
                        onContinue();
                    }}
                    customDescriptionWebhookSuccess="We have reviewed your insurance details and found that your current insurance plan is not compatible with MedBox. Please check your email for important information."
                    customDescriptionWebhookFailure="We have reviewed your insurance details and found that your current insurance plan is not compatible with MedBox."
                />
            </div>
        </InsuranceContext.Provider>
    );
};

export default InsuranceOutletWithContext;
